import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Box, Tooltip, Typography, useTheme } from '@mui/material';
const PLACEHOLDER_IMAGE = '/placeholder.svg?height=24&width=24';
const getInitialsFromLabel = (label) => {
    if (!label || !label.trim())
        return '?';
    const parts = label.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }
    return (label[0]?.toUpperCase() ?? '?') + (label[1]?.toUpperCase() ?? '');
};
const getAvatarSrc = (image) => {
    if (image && image !== PLACEHOLDER_IMAGE)
        return image;
    return undefined;
};
const GenericAvatarGroup = ({ items, maxDisplay = 3, size = 32, onClick, showTooltip = false, showLabelForSingle = false, sx }) => {
    const theme = useTheme();
    if (items.length === 0) {
        return null;
    }
    // Single item with optional label beside it
    if (items.length === 1 && showLabelForSingle) {
        const item = items[0];
        const src = getAvatarSrc(item.image);
        const avatar = (_jsx(Avatar, { src: src, alt: item.label, sx: {
                width: size,
                height: size,
                border: `2px solid ${theme.palette.background.paper}`,
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.neutral,
                color: '#637381'
            }, children: !src ? getInitialsFromLabel(item.label) : null }));
        return (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: onClick ? 'pointer' : 'default',
                ...sx
            }, onClick: onClick, children: [showTooltip ? (_jsx(Tooltip, { title: item.label, arrow: true, children: avatar })) : (avatar), _jsx(Typography, { variant: 'body1', color: theme.palette.text.primary, children: item.label })] }));
    }
    // Multiple items
    const displayItems = items.slice(0, maxDisplay);
    const remainingCount = items.length - maxDisplay;
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minWidth: remainingCount > 0
                ? size * maxDisplay + 8 + String(remainingCount).length * 10
                : size * displayItems.length,
            pr: remainingCount > 0 ? 1 : 0,
            overflow: 'visible',
            cursor: onClick ? 'pointer' : 'default',
            ...sx
        }, onClick: onClick, children: [displayItems.map((item, idx) => {
                const src = getAvatarSrc(item.image);
                const avatar = (_jsx(Avatar, { src: src, alt: item.label, sx: {
                        width: size,
                        height: size,
                        ml: idx === 0 ? 0 : '-8px',
                        border: `2px solid ${theme.palette.background.paper}`,
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: 1.5,
                        zIndex: maxDisplay - idx,
                        backgroundColor: theme.palette.background.neutral,
                        color: '#637381'
                    }, children: !src ? getInitialsFromLabel(item.label) : null }, item.id ?? idx));
                if (showTooltip) {
                    return (_jsx(Tooltip, { title: item.label, arrow: true, children: avatar }, item.id ?? idx));
                }
                return avatar;
            }), remainingCount > 0 && (_jsxs(Avatar, { sx: {
                    width: size,
                    height: size,
                    ml: '-8px',
                    fontSize: '12px',
                    borderRadius: 1.5,
                    fontWeight: 500,
                    border: `2px solid ${theme.palette.background.paper}`,
                    backgroundColor: theme.palette.primary.lighter,
                    color: theme.palette.primary.dark,
                    zIndex: 0
                }, children: ["+", remainingCount] }))] }));
};
export default GenericAvatarGroup;
